import "./style.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import InventoryIcon from "@mui/icons-material/Inventory";
import ScienceIcon from "@mui/icons-material/Science";

import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Select, MenuItem, Autocomplete, TextField } from "@mui/material";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import { PieGraph, BarGraph } from "./Graphs";
import { DisciplineOptions } from "../disciplineMaster/disiplineConst";
import { hasAccessToModule } from "../rbac/hasAccess";
import clientBg from "../image/client.png";

//  StatCard - overall, today and for work report 
function StatCard({ 
  title, icon, link, 
  curr, prev, total,
  today, todayLabel = "Count", subLabel 
}) {
  const navigate = useNavigate();
  const isMonthly = curr !== undefined;

  // Calculate diff
  let diff = 0, pct = "0", isPositive = true;
  if (isMonthly) {
    diff = curr - (prev || 0);
    pct = (prev !== 0 && prev !== undefined) ? ((diff / prev) * 100).toFixed(1) : (curr > 0 ? "100" : "0");
    isPositive = diff >= 0;
  }

  return (
    <div
      className={`common-stat-card ${isMonthly ? 'monthly' : 'simple'} stat-card--clickable`}
      onClick={link ? () => navigate(link) : undefined}
    >
      <div className="csc-header">
        <span className="csc-icon-wrap">{icon}</span>
        <span className="csc-title">{title}</span>
      </div>

      {isMonthly ? (
        <>
          <div className="csc-body">
            <div className="csc-row">
              <span className="csc-label">This Month</span>
              <span className="csc-value primary">{curr}</span>
            </div>
            <div className="csc-row">
              <span className="csc-label">Last Month</span>
              <span className="csc-value">{prev || 0}</span>
            </div>
            <div className="csc-row">
              <span className="csc-label">Total</span>
              <span className="csc-value">{total || 0}</span>
            </div>
          </div>
          <div className="csc-footer">
            <span className="csc-f-label">Change</span>
            <div className="csc-f-right">
              <span className={`csc-diff ${isPositive ? "pos" : "neg"}`}>
                {isPositive ? "+" : ""}{diff}
              </span>
              <span className={`csc-chip ${isPositive ? "pos" : "neg"}`}>
                {isPositive ? "+" : ""}{pct}%
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          {subLabel && <div className="csc-sub">{subLabel}</div>}
          <div className="csc-bottom">
            <span className="csc-label">{todayLabel}</span>
            <span className="csc-value">{today || 0}</span>
          </div>
        </>
      )}
    </div>
  );
}

//  GraphCard - Discipline, Approval, and  for Leave Statistics
function GraphCard({ controls, chart, summaryTitle, stats, footerNote, children }) {
  return (
    <div className="common-graph-card">
      {controls && <div className="cgc-controls">{controls}</div>}
      <div className="cgc-content">
        <div className="cgc-chart-side">
          {chart}
        </div>
        <div className="cgc-info-side">
          <h4 className="cgc-info-title">{summaryTitle}</h4>
          <div className="cgc-stats-grid">
            {stats.map((s, i) => (
              <div 
                key={i} 
                className="cgc-stat-item"
                style={{ backgroundColor: s.bg, color: s.color }}
              >
                <span className="cgc-label" style={{ color: s.color }}>{s.label}</span>
                <span className="cgc-value" style={{ color: s.color }}>{s.value}</span>
              </div>
            ))}
          </div>
          {children}
          {footerNote && (
            <div className="cgc-footer-note">
              <span className="cgc-footer-icon" style={footerNote.iconColor ? { backgroundColor: footerNote.iconColor } : {}} /> 
              {footerNote.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
//  ProjectProgressBar - horizontal bar for projects statistics
function ProjectProgressBar({ stats, label }) {
  const segments = [
    { label: "Completed", value: stats.completed, percentage: stats.percentages.completed, color: "#2e7d32", textColor: "#fff" },
    { label: "Ongoing", value: stats.ongoing, percentage: stats.percentages.ongoing, color: "#1976d2", textColor: "#fff" },
    { label: "Pending", value: stats.pending, percentage: stats.percentages.pending, color: "#94a3b8", textColor: "#fff" },
  ];

  return (
    <div className="common-graph-card project-stats-card">
      <div className="project-stats-header">
        <div className="project-header-top">
          <div className="summary-counts">
            <div className="count-item">
              <span className="count-val green">{stats.completed}</span>
              <span className="count-lbl">Completed</span>
            </div>
            <span className="count-sep">/</span>
            <div className="count-item">
              <span className="count-val blue">{stats.ongoing}</span>
              <span className="count-lbl">Ongoing</span>
            </div>
            <span className="count-sep">/</span>
            <div className="count-item">
              <span className="count-val grey">{stats.pending}</span>
              <span className="count-lbl">Pending</span>
            </div>
          </div>
          <div className="project-stats-total">
            Total: {stats.total}
          </div>
        </div>

        <div className="project-stats-body">
          <div className="project-vertical-label">PROJECTS</div>
          <div className="project-bar-container">
            {segments.map((seg) => (
              <div
                key={seg.label}
                className="project-bar-segment"
                style={{
                  width: `${seg.percentage}%`,
                  backgroundColor: seg.color,
                  color: seg.textColor,
                }}
                title={`${seg.label}: ${seg.value}`}
              >
                {seg.percentage >= 8 && seg.value > 0 ? seg.value : ""}
              </div>
            ))}
          </div>
        </div>

        <div className="project-stats-legend">
          {segments.map((seg) => (
            <div key={seg.label} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: seg.color }}></span>
              <span className="legend-label">{seg.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


  // SectionHeader 

function SectionHeader({ title, action }) {
  return (
    <div className="section-header">
      <div className="sh-left">
        <span className="sh-bar" />
        <h2 className="sh-title">{title}</h2>
      </div>
      {action && <div className="sh-action">{action}</div>}
    </div>
  );
}

const safeNum = (rows, key) => Number(rows?.[0]?.[key]) || 0;

//  DASHBOARD COMPONENT
export default function Dashboard() {
  const userType = Number(localStorage.getItem("type")) || 0;
  const isClient = userType === 13;
  const userId = localStorage.getItem("id") || "";
  const employeeId = localStorage.getItem("employeeId") || "";
  const isAdmin = [1, 2, 3].includes(userType);
  const isSuperAdmin = [1, 2].includes(userType);  // type 1 & 2: full access view
  const isHOD = userType === 3;   // type 3:
  
  //check localstorage 
  const userModules = (localStorage.getItem("modules") || "")
    .split(",")
    .map((m) => m.trim());
  const hasProjectModuleAccess = userModules.includes("13");
  //only if engineer & etc have accessmodule 13
  const canSeeProjects = isAdmin || hasProjectModuleAccess;

  // only inwards that have tests in the HOD's groups
  const hodGroupIds = localStorage.getItem("groupId") || "";

  //  Overall Statistics (monthly comparison )
  const emptyMonthly = { curr: 0, prev: 0, total: 0 };
  const [monthlyStats, setMonthlyStats] = useState({
    employee:    { ...emptyMonthly },
    job:         { ...emptyMonthly },
    masterRenewal: { ...emptyMonthly },
    requirement: { ...emptyMonthly },
    inward:      { ...emptyMonthly },
    observation: { ...emptyMonthly },
    certificate: { ...emptyMonthly },
  });

 // projects statistic
  const [projectsTimeframe, setProjectsTimeframe] = useState("today");
  const [projectsStats, setProjectsStats] = useState({
    total: 0,
    completed: 0,
    ongoing: 0,
    pending: 0,
    percentages: { completed: 0, ongoing: 0, pending: 0 },
  });

 
  //  Today Activity 
  const [todayStats, setTodayStats] = useState({
    inward: 0,
    certificate: 0,
    observation: 0,
    requirement: 0,
  });

  //  Work Report not in use currently 
  const [workReportTimeframe, setWorkReportTimeframe] = useState("monthly");
  const [topObserver, setTopObserver] = useState({ name: "N/A", count: 0 });
  const [minObserver, setMinObserver] = useState({ name: "N/A", count: 0 });
  const [topApprover, setTopApprover] = useState({ name: "N/A", count: 0 });
  const [minApprover, setMinApprover] = useState({ name: "N/A", count: 0 });

  //  Discipline Statistics 
  const [disciplineTimeframe, setDisciplineTimeframe] = useState("today");
  const [disciplinesList, setDisciplinesList] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [disciplineStats, setDisciplineStats] = useState({
    total: 0, completed: 0, pending: 0,
  });

  const [disciplinePieData, setDisciplinePieData] = useState(buildPieData(
    ["Completed", "Pending"],
    [0, 0],
    ["rgba(25,118,210,1)", "rgba(100,181,246,1)"]
  ));

  //  Certificates Approval 
  const [certApprovalTimeframe, setCertApprovalTimeframe] = useState("today");
  const [certApprovalStats, setCertApprovalStats] = useState({
    total: 0, approved: 0, pending: 0,
  });
  const [certApprovalPieData, setCertApprovalPieData] = useState(buildPieData(
    ["Approved", "Pending"],
    [0, 0],
    ["rgb(25, 118, 210)", "rgb(162, 211, 250)"]
  ));

  //  Requirement Approval
  const [reqApprovalTimeframe, setReqApprovalTimeframe] = useState("today");
  const [reqApprovalStats, setReqApprovalStats] = useState({
    total: 0, approved: 0, pending: 0, rejected: 0, disbursed: 0,
  });
  const [reqApprovalPieData, setReqApprovalPieData] = useState(buildPieData(
    ["Approved", "Pending", "Rejected", "Disbursed"],
    [0, 0, 0, 0],
    ["rgba(156,39,176,1)", "rgba(144,164,174,1)", "rgba(211,47,47,1)", "rgba(76,175,80,1)"]
  ));

  //  Leave Statistics 
  const [leaveTimeframe, setLeaveTimeframe] = useState("today");
  const [leaveStats, setLeaveStats] = useState({
    total: 0, approved: 0, pending: 0, rejected: 0,
  });
  const [leavePieData, setLeavePieData] = useState(buildPieData(
    ["Approved", "Pending", "Rejected"],
    [0, 0, 0],
    ["rgba(46,125,50,1)", "rgba(255,167,38,1)", "rgba(211,47,47,1)"]
  ));

  //  dataset object
  function buildPieData(labels, data, colors) {
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    };
  }


  //  DATA FETCH

  // 1. Overall Statistics — single query
  const fetchMonthlyStats = async () => {
    try {
      // api call based on user role
      const inwardFilter = isAdmin ? "" : ` AND createdBy = ${userId}`;
      const observationFilter = isAdmin ? "" : ` AND testedBy = ${employeeId}`;
      const certificateFilter = isAdmin ? "" : ` AND authorizedBy = ${employeeId}`;
      const requirementFilter = isAdmin ? "" : ` AND requesterId = ${employeeId}`;

      const res = await fetchQuery(`
      SELECT 'employee' AS cat,
        COUNT(*) AS total,
        SUM(CASE WHEN dateOfJoining >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND dateOfJoining < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END) AS curr,
        SUM(CASE WHEN dateOfJoining >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND dateOfJoining < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END) AS prev
      FROM employees WHERE status = 1

      UNION ALL

      SELECT 'job',
        COUNT(*),
        SUM(CASE WHEN jobDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND jobDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN jobDate >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND jobDate < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM jobRegister WHERE status = 1

      UNION ALL

      SELECT 'masterRenewal',
        COUNT(*),
        SUM(CASE WHEN dueDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND dueDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN dueDate >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND dueDate < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM equipmentsMaster WHERE status = 1 AND dueDate IS NOT NULL

      UNION ALL

      SELECT 'requirement',
        COUNT(*),
        SUM(CASE WHEN date >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND date < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN date >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND date < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM requirements WHERE status != -1${requirementFilter}

      UNION ALL

      SELECT 'inward',
        COUNT(*),
        SUM(CASE WHEN inwardDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND inwardDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN inwardDate >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND inwardDate < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM inward WHERE status = 1${inwardFilter}

      UNION ALL

      SELECT 'observation',
        COUNT(*),
        SUM(CASE WHEN startTestingDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND startTestingDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN startTestingDate >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND startTestingDate < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM observationSheet WHERE status != -1${observationFilter}

      UNION ALL

      SELECT 'certificate',
        COUNT(*),
        SUM(CASE WHEN authorizedDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND authorizedDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') THEN 1 ELSE 0 END),
        SUM(CASE WHEN authorizedDate >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01') AND authorizedDate < DATE_FORMAT(CURDATE(), '%Y-%m-01') THEN 1 ELSE 0 END)
      FROM certificate WHERE status != -1${certificateFilter}
      `);

      const map = {};
      (res || []).forEach((row) => {
        map[row.cat] = {
          curr:  Number(row.curr)  || 0,
          prev:  Number(row.prev)  || 0,
          total: Number(row.total) || 0,
        };
      });

      setMonthlyStats((prev) => ({
        employee:      map.employee      || prev.employee,
        job:           map.job           || prev.job,
        masterRenewal: map.masterRenewal || prev.masterRenewal,
        requirement:   map.requirement   || prev.requirement,
        inward:        map.inward        || prev.inward,
        observation:   map.observation   || prev.observation,
        certificate:   map.certificate   || prev.certificate,
      }));
    } catch (err) {
      console.error("fetchMonthlyStats error:", err);
    }
  };

  //  Today's Activity
  const fetchTodayStats = async () => {
    try {
      const inwardFilter = isAdmin ? "" : ` AND createdBy = ${userId}`;
      const observationFilter = isAdmin ? "" : ` AND testedBy = ${employeeId}`;
      const certificateFilter = isAdmin ? "" : ` AND authorizedBy = ${employeeId}`;
      const requirementFilter = isAdmin ? "" : ` AND requesterId = ${employeeId}`;

      const res = await fetchQuery(`
        SELECT 'inward' AS cat, COUNT(*) AS cnt FROM inward WHERE status = 1 AND inwardDate >= CURDATE() AND inwardDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)${inwardFilter}
        UNION ALL
        SELECT 'certificate', COUNT(*) FROM certificate WHERE status != -1 AND authorizedDate >= CURDATE() AND authorizedDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)${certificateFilter}
        UNION ALL
        SELECT 'observation', COUNT(*) FROM observationSheet WHERE status != -1 AND startTestingDate >= CURDATE() AND startTestingDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)${observationFilter}
        UNION ALL
        SELECT 'requirement', COUNT(*) FROM requirements WHERE status != -1 AND \`date\` >= CURDATE() AND \`date\` < DATE_ADD(CURDATE(), INTERVAL 1 DAY)${requirementFilter}
      `);

      const map = {};
      (res || []).forEach((row) => {
        map[row.cat] = Number(row.cnt) || 0;
      });

      setTodayStats({
        inward:      map.inward || 0,
        certificate: map.certificate || 0,
        observation: map.observation || 0,
        requirement: map.requirement || 0,
      });
    } catch (err) {
      console.error("fetchTodayStats error:", err);
    }
  };

  // 6. Work Report 
  const fetchWorkReport = async (timeframe) => {
    const condition =
      timeframe === "monthly"
        ? "AND os.startTestingDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01')"
        : "";

    const certCondition =
      timeframe === "monthly"
        ? "AND c.authorizedDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01')"
        : "";

    try {
      const [obsPerf, certPerf] = await Promise.all([
        fetchQuery(`
          SELECT e.id, e.name,
                 COUNT(os.id) AS workCount
          FROM employees e
          INNER JOIN observationSheet os ON os.testedBy = e.id
          WHERE os.status != -1 ${condition}
          GROUP BY e.id, e.name
          ORDER BY workCount DESC
        `),
        fetchQuery(`
          SELECT e.id, e.name,
                 COUNT(c.id) AS workCount
          FROM employees e
          INNER JOIN certificate c ON c.authorizedBy = e.id
          WHERE c.status != -1 ${certCondition}
          GROUP BY e.id, e.name
          ORDER BY workCount DESC
        `),
      ]);

      if (obsPerf.length > 0) {
        setTopObserver({ name: obsPerf[0].name, count: obsPerf[0].workCount });
        setMinObserver({
          name: obsPerf[obsPerf.length - 1].name,
          count: obsPerf[obsPerf.length - 1].workCount,
        });
      } else {
        setTopObserver({ name: "N/A", count: 0 });
        setMinObserver({ name: "N/A", count: 0 });
      }

      if (certPerf.length > 0) {
        setTopApprover({ name: certPerf[0].name, count: certPerf[0].workCount });
        setMinApprover({
          name: certPerf[certPerf.length - 1].name,
          count: certPerf[certPerf.length - 1].workCount,
        });
      } else {
        setTopApprover({ name: "N/A", count: 0 });
        setMinApprover({ name: "N/A", count: 0 });
      }
    } catch (err) {
      console.error("fetchWorkReport error:", err);
    }
  };

  //  Discipline Statistics
  const fetchDisciplines = async () => {
    try {
      const res = await fetchQuery(
        `SELECT * FROM discipline WHERE status = 1`
      );
      const mapped = (res || []).map(r => {
        const foundLabel = DisciplineOptions.find(opt => opt.id == r.name)?.label;
        const discStr = foundLabel || r.discipline || r.disciplineName || r.name || `Disc ${r.id}`;
        const groupStr = r.groupName || r.group || "";
        
        return {
          id: r.id, 
          name: groupStr ? `${discStr} (${groupStr})` : discStr
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
      
      setDisciplinesList(mapped);
      if (mapped.length > 0) {
        setSelectedDiscipline(mapped[0].id); // store ID not object
        fetchDisciplineStats(mapped[0].id, disciplineTimeframe);
      }
    } catch (err) {
      console.error("fetchDisciplines error:", err);
    }
  };

  const fetchDisciplineStats = async (discId, timeframe) => {
    if (!discId) return;

    let dateCondition = "";
    if (timeframe === "today") {
      dateCondition = "AND os.startTestingDate >= CURDATE() AND os.startTestingDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)";
    } else if (timeframe === "monthly") {
      dateCondition = "AND os.startTestingDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND os.startTestingDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')";
    }

    try {
      const [totalRes, completedRes, pendingRes] = await Promise.all([
        fetchQuery(
          `SELECT COUNT(os.id) AS cnt FROM observationSheet os
           INNER JOIN inwardTests it ON os.inwardTestId = it.id
           WHERE os.status != -1 AND it.groupId = ${discId} ${dateCondition}`
        ),
        fetchQuery(
          `SELECT COUNT(os.id) AS cnt FROM observationSheet os
           INNER JOIN inwardTests it ON os.inwardTestId = it.id
           WHERE os.status != -1 AND it.groupId = ${discId}
           AND os.totalReadings IS NOT NULL AND os.totalReadings > 0 ${dateCondition}`
        ),
        fetchQuery(
          `SELECT COUNT(os.id) AS cnt FROM observationSheet os
           INNER JOIN inwardTests it ON os.inwardTestId = it.id
           WHERE os.status != -1 AND it.groupId = ${discId}
           AND (os.totalReadings IS NULL OR os.totalReadings <= 0) ${dateCondition}`
        ),
      ]);

      const total = safeNum(totalRes, "cnt");
      const completed = safeNum(completedRes, "cnt");
      const pending = safeNum(pendingRes, "cnt");

      setDisciplineStats({ total, completed, pending });
      setDisciplinePieData(
        buildPieData(
          ["Completed", "Pending"],
          [completed, pending],
          ["rgb(25, 118, 210)", "rgb(100, 181, 246)"]
        )
      );
    } catch (err) {
      console.error("fetchDisciplineStats error:", err);
    }
  };

  // Certificate Approval
  const fetchCertApprovalStats = async (timeframe) => {
    let condition = "1=1";
    if (timeframe === "today") {
      condition = "c.authorizedDate >= CURDATE() AND c.authorizedDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)";
    } else if (timeframe === "monthly") {
      condition = "c.authorizedDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND c.authorizedDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')";
    }

    const certificateFilter = isAdmin ? "" : ` AND c.authorizedBy = ${employeeId}`;

    try {
      const res = await fetchQuery(`
        SELECT
          COUNT(*) AS total,
          SUM(CASE WHEN c.authorizedBy IS NOT NULL AND ${condition} THEN 1 ELSE 0 END) AS approved,
          SUM(CASE WHEN c.authorizedBy IS NULL THEN 1 ELSE 0 END) AS pending
        FROM certificate c
        WHERE c.status != -1${certificateFilter}
      `);
      const row = res[0] || {};
      const total = Number(row.total) || 0;
      const approved = Number(row.approved) || 0;
      const pending = Number(row.pending) || 0;

      setCertApprovalStats({ total, approved, pending });
      setCertApprovalPieData(
        buildPieData(
          ["Approved", "Pending"],
          [approved, pending],
          ["rgb(25, 118, 210)", "rgb(100, 181, 246)"]
        )
      );
    } catch (err) {
      console.error("fetchCertApprovalStats error:", err);
    }
  };

  // 9. Requirement Approval stats
  const fetchReqApprovalStats = async (timeframe) => {
    let condition = "";
    if (timeframe === "today") {
      condition = "AND r.date >= CURDATE() AND r.date < DATE_ADD(CURDATE(), INTERVAL 1 DAY)";
    } else if (timeframe === "monthly") {
      condition = "AND r.date >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND r.date < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')";
    }

    const reqFilter = isAdmin ? "" : ` AND r.requesterId = ${employeeId}`;

    try {
      const res = await fetchQuery(`
        SELECT
          COUNT(*) AS total,
          SUM(CASE WHEN r.h2Status = 1 THEN 1 ELSE 0 END) AS approved,
          SUM(CASE WHEN (r.l1Status = 2 OR r.hodStatus = 2 OR r.h1Status = 2 OR r.h2Status = 2) THEN 1 ELSE 0 END) AS rejected,
        SUM(CASE WHEN r.disbursedStatus = 1 THEN 1 ELSE 0 END) AS disbursed,
          SUM(CASE WHEN r.h2Status = 0 
                    AND r.l1Status != 2 AND r.hodStatus != 2 AND r.h1Status != 2 AND r.h2Status != 2 
               THEN 1 ELSE 0 END) AS pending
        FROM requirements r
        WHERE r.status != -1 ${condition}${reqFilter}
      `);
      
      const row = res[0] || {};
      const total = Number(row.total) || 0;
      const approved = Number(row.approved) || 0;
      const pending = Number(row.pending) || 0;
      const rejected = Number(row.rejected) || 0;
      const disbursed = Number(row.disbursed) || 0;

      setReqApprovalStats({ total, approved, pending, rejected, disbursed });
      setReqApprovalPieData(
        buildPieData(
          ["Approved", "Pending", "Rejected", "Disbursed"],
          [approved, pending, rejected, disbursed],
          [
            "rgb(25, 118, 210)", 
            "rgb(100, 181, 246)", 
            "rgb(144, 164, 174)",
            "rgb(46, 125, 50)"
          ]
        )
      );
    } catch (err) {
      console.error("fetchReqApprovalStats error:", err);
    }
  };

  // Leave Statistics
  const fetchLeaveStats = async (timeframe = "monthly") => {
    let condition = "";
    if (timeframe === "today") {
      condition = "AND applyLeaveDate >= CURDATE() AND applyLeaveDate < DATE_ADD(CURDATE(), INTERVAL 1 DAY)";
    } else if (timeframe === "monthly") {
      condition = "AND applyLeaveDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') AND applyLeaveDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')";
    }

    const leaveFilter = isAdmin ? "" : ` AND employeeId = ${employeeId}`;

    try {
      const res = await fetchQuery(`
        SELECT
          COUNT(*) AS total,
          SUM(CASE WHEN approveLevel2 = 2 THEN 1 ELSE 0 END) AS approved,
          SUM(CASE WHEN (approveManagerStatus = 0 OR approveLevel1 = 0 OR approveLevel2 = 0)
                    AND (approveManagerStatus != 3 AND approveLevel1 != 3 AND approveLevel2 != 3)
                    AND approveLevel2 != 2 THEN 1 ELSE 0 END) AS pending,
          SUM(CASE WHEN approveManagerStatus = 3 OR approveLevel1 = 3 OR approveLevel2 = 3 THEN 1 ELSE 0 END) AS rejected
        FROM leaveManagement
        WHERE status = 1 ${condition}${leaveFilter}
      `);
      const row = res[0] || {};
      const total = Number(row.total) || 0;
      const approved = Number(row.approved) || 0;
      const pending = Number(row.pending) || 0;
      const rejected = Number(row.rejected) || 0;

      setLeaveStats({ total, approved, pending, rejected });
      setLeavePieData(
        buildPieData(
          ["Approved", "Pending", "Rejected"],
          [approved, pending, rejected],
          [      
            "rgb(25, 118, 210)",
            "rgb(100, 181, 246)",
            "rgb(144, 164, 174)",
          
          ]
        )
      );
    } catch (err) {
      console.error("fetchLeaveStats error:", err);
    }
  };

// Fetch Project stats
// Admin (types 1 & 2 shows evertyhing and HOD shows based on disciple and groupid
// Non-admin with moduleAccess 13: filtered to their own records only
  const fetchProjectsStats = async (timeframe) => {
    let dateCondition = "";

    if (timeframe === "today") {
      dateCondition = `
        AND DATE(i.inwardDate) = CURDATE()
      `;
    } else if (timeframe === "monthly") {
      dateCondition = `
        AND i.inwardDate >= DATE_FORMAT(CURDATE(), '%Y-%m-01') 
        AND i.inwardDate < DATE_FORMAT(DATE_ADD(CURDATE(), INTERVAL 1 MONTH), '%Y-%m-01')
      `;
    }

    // Role-based filtering
    let inwardFilter = "";

    if (isSuperAdmin) {
      inwardFilter = "";
    } else if (isHOD && hodGroupIds) {
      inwardFilter = `
        AND EXISTS (
          SELECT 1 
          FROM inwardTests it
          WHERE it.inwardId = i.id
            AND it.status != -1
            AND FIND_IN_SET(it.groupId, '${hodGroupIds}') > 0
        )
      `;
    } else {
      inwardFilter = `AND i.createdBy = ${userId}`;
    }

    try {
      const [totalRes, completedRes, pendingRes] = await Promise.all([

        // ✅ TOTAL
        fetchQuery(`
          SELECT COUNT(*) AS cnt
          FROM inward i
          WHERE i.status = 1
          ${dateCondition}
          ${inwardFilter}
        `),

        // ✅ COMPLETED
        fetchQuery(`
          SELECT COUNT(DISTINCT i.id) AS cnt
          FROM inward i
          WHERE i.status = 1
          ${dateCondition}
          ${inwardFilter}

          -- must have at least one certificate
          AND EXISTS (
            SELECT 1
            FROM inwardTests it
            JOIN observationSheet os 
              ON FIND_IN_SET(it.id, os.inwardTestId)
            AND os.status != -1
            JOIN certificate cert 
              ON cert.id = os.certificateId
            AND cert.status != -1
            WHERE it.inwardId = i.id
              AND it.status != -1
          )

          -- none should be unauthorized
          AND NOT EXISTS (
            SELECT 1
            FROM inwardTests it
            JOIN observationSheet os 
              ON FIND_IN_SET(it.id, os.inwardTestId)
            AND os.status != -1
            JOIN certificate cert 
              ON cert.id = os.certificateId
            AND cert.status != -1
            WHERE it.inwardId = i.id
              AND it.status != -1
              AND cert.authorizedBy IS NULL
          )
        `),

        // ✅ PENDING
        fetchQuery(`
          SELECT COUNT(DISTINCT i.id) AS cnt
          FROM inward i
          WHERE i.status = 1
          ${dateCondition}
          ${inwardFilter}

          -- no readings anywhere
          AND NOT EXISTS (
            SELECT 1
            FROM inwardTests it
            JOIN observationSheet os 
              ON FIND_IN_SET(it.id, os.inwardTestId)
            AND os.status != -1
            WHERE it.inwardId = i.id
              AND it.status != -1
              AND os.totalReadings IS NOT NULL
              AND os.totalReadings > 0
          )
        `)

      ]);

      const safeNum = (rows, key) => Number(rows?.[0]?.[key]) || 0;

      const total     = safeNum(totalRes, "cnt");
      const completed = safeNum(completedRes, "cnt");
      const pending   = safeNum(pendingRes, "cnt");

      // ✅ BEST PRACTICE (avoid buggy SQL for ongoing)
      const ongoing = Math.max(0, total - completed - pending);

      setProjectsStats({
        total,
        completed,
        ongoing,
        pending,
        percentages: {
          completed: total > 0 ? Number(((completed / total) * 100).toFixed(1)) : 0,
          ongoing:   total > 0 ? Number(((ongoing   / total) * 100).toFixed(1)) : 0,
          pending:   total > 0 ? Number(((pending   / total) * 100).toFixed(1)) : 0,
        },
      });

    } catch (err) {
      console.error("fetchProjectsStats error:", err);
    }
  };

  //  Initial load
  useEffect(() => {
    fetchMonthlyStats();
    fetchTodayStats();
    fetchLeaveStats("today");
    fetchCertApprovalStats("today");
    fetchReqApprovalStats("today");
    if (canSeeProjects) {
      fetchProjectsStats("today");
    }

    // Only load these if user is admin
    if (isAdmin) {
      fetchWorkReport("monthly");
      fetchDisciplines();
    }
  }, [userType]);

  //  Rrender dashboard
  return (
    <div 
      className="dashboard-new"
      style={isClient ? { height: "calc(100vh - 70px)", overflow: "hidden" } : {}}
    >

    {/* client view */}
    {isClient ? (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={clientBg}
          alt="Client Dashboard"
          style={{
            height: "100%",
            maxHeight: "100%",
            maxWidth: "100%",
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      </div>    ) : (
      <>

      {/* OVERALL STATISTICS 7 cards monthly view   */}
      <SectionHeader title="Overall Statistics" />
      <div className="card-grid">
        {isAdmin && (
          <>
            <StatCard
              title="Employees"
              icon={<PeopleIcon className="db-icon-blue" />}
              curr={monthlyStats.employee.curr}
              prev={monthlyStats.employee.prev}
              total={monthlyStats.employee.total}
              link="/master/employeeMaster"
            />
            <StatCard
              title="Job Register"
              icon={<AssignmentIcon className="db-icon-amber" />}
              curr={monthlyStats.job.curr}
              prev={monthlyStats.job.prev}
              total={monthlyStats.job.total}
              link="/jobRegister"
            />
            <StatCard
              title="Master Renewal"
              icon={<AutorenewIcon className="db-icon-green" />}
              curr={monthlyStats.masterRenewal.curr}
              prev={monthlyStats.masterRenewal.prev}
              total={monthlyStats.masterRenewal.total}
              link="/master/masterEquipment"
            />
          </>
        )}
       {hasAccessToModule(10) && <StatCard
          title="Requirements"
          icon={<InventoryIcon className="db-icon-purple" />}
          curr={monthlyStats.requirement.curr}
          prev={monthlyStats.requirement.prev}
          total={monthlyStats.requirement.total}
          link="/requirementList"
        />}
        <StatCard
          title="Inward"
          icon={<AssignmentIcon className="db-icon-amber" />}
          curr={monthlyStats.inward.curr}
          prev={monthlyStats.inward.prev}
          total={monthlyStats.inward.total}
          link="/inwardList"
        />
        <StatCard
          title="Observation Sheet"
          icon={<ScienceIcon className="db-icon-blue" />}
          curr={monthlyStats.observation.curr}
          prev={monthlyStats.observation.prev}
          total={monthlyStats.observation.total}
          link="/datasheetList"
        />
        <StatCard
          title="Certificate"
          icon={<WorkspacePremiumIcon className="db-icon-blue" />}
          curr={monthlyStats.certificate.curr}
          prev={monthlyStats.certificate.prev}
          total={monthlyStats.certificate.total}
          link="/certificateList"
        />
      </div>

  {/* PROJECTS statistic - horizontal bar */}
  {canSeeProjects && (
        <>
          <SectionHeader
        title="Projects Statistics"
            action={
              <Select
                value={projectsTimeframe}
                onChange={(e) => {
                  setProjectsTimeframe(e.target.value);
                  fetchProjectsStats(e.target.value);
                }}
                size="small"
                className="dashboard-select"
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="overall">Overall</MenuItem>
              </Select>
            }
          />
          <ProjectProgressBar
            stats={projectsStats}
            label="Projects"
          />
        </>
      )}
  
      {/* TODAY'S ACTIVITY */}

      <SectionHeader title="Today's Activity" />
      <div className="card-grid">
        <StatCard
          title="Inward"
          icon={<AssignmentIcon className="db-icon-orange" />}
          today={todayStats.inward}
          todayLabel="Today's Count"
        />
        <StatCard
          title="Certificate"
          icon={<WorkspacePremiumIcon className="db-icon-orange" />}
          today={todayStats.certificate}
          todayLabel="Today's Count"
        />
        <StatCard
          title="Observation"
          icon={<ScienceIcon className="db-icon-orange" />}
          today={todayStats.observation}
          todayLabel="Today's Count"
        />
      {hasAccessToModule(10) && <StatCard
          title="Requirement"
          icon={<InventoryIcon className="db-icon-orange" />}
          today={todayStats.requirement}
          todayLabel="Today's Count"
        />}
      </div>

           {/* WORK REPORT not in use currently */}

      {/* {isAdmin && (
        <>
          <SectionHeader
            title="Work Report"
            action={
              <Select
                value={workReportTimeframe}
                onChange={(e) => {
                  setWorkReportTimeframe(e.target.value);
                  fetchWorkReport(e.target.value);
                }}
                size="small"
                className="dashboard-select"
              >
                <MenuItem value="overall">Overall</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
              </Select>
            }
          />
          <div className="card-grid">
            <StatCard
              title="Top Observer"
              icon={<EmojiEventsIcon className="db-icon-green" />}
              subLabel={topObserver.name}
              today={topObserver.count}
              todayLabel="Observations"
              link="/observationSheetList"
            />
            <StatCard
              title="Lowest Observer"
              icon={<TrendingDownIcon className="db-icon-orange" />}
              subLabel={minObserver.name}
              today={minObserver.count}
              todayLabel="Observations"
              link="/observationSheetList"
            />
            <StatCard
              title="Top Approver"
              icon={<EmojiEventsIcon className="db-icon-blue" />}
              subLabel={topApprover.name}
              today={topApprover.count}
              todayLabel="Approvals"
              link="/certificateList"
            />
            <StatCard
              title="Lowest Approver"
              icon={<TrendingDownIcon className="db-icon-purple" />}
              subLabel={minApprover.name}
              today={minApprover.count}
              todayLabel="Approvals"
              link="/certificateList"
            />
          </div>
        </>
      )} */}

          {/* DISCIPLINE STATISTICS  */}

      {isAdmin && (
        <>
          <SectionHeader 
            title="Discipline Statistics"
            action={
              <Select
                value={disciplineTimeframe}
                onChange={(e) => {
                  setDisciplineTimeframe(e.target.value);
                  fetchDisciplineStats(selectedDiscipline, e.target.value);
                }}
                size="small"
                className="dashboard-select"
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="overall">Overall</MenuItem>
              </Select>
            }
          />
          <GraphCard
            controls={
              <Autocomplete
                options={disciplinesList}
                getOptionLabel={(o) => o.name || ""}
                value={disciplinesList.find(d => d.id === selectedDiscipline) || null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, newVal) => {
                  if (newVal) {
                    setSelectedDiscipline(newVal.id);
                    fetchDisciplineStats(newVal.id, disciplineTimeframe);
                  }
                }}
                size="small"
                sx={{ width: 350, maxWidth: '100%' }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Discipline" variant="outlined" />
                )}
              />
            }
            chart={<PieGraph data={disciplinePieData} />}
            summaryTitle={
              selectedDiscipline && disciplinesList.find(d => d.id === selectedDiscipline)
                ? `Observation Summary - ${disciplinesList.find(d => d.id === selectedDiscipline).name}`
                : "Select a Discipline"
            }
            stats={[
              { label: "Total Calibration", value: disciplineStats.total, color: "#283593", bg: "rgba(40,53,147,0.08)" },
              { label: "Completed Calibration", value: disciplineStats.completed, color: "#1976d2", bg: "rgba(25,118,210,0.08)" },
              { label: "Pending Calibration", value: disciplineStats.pending, color: "#64b5f6", bg: "rgba(100,181,246,0.15)" },
            ]}
            footerNote={{ text: "Database statistics for the selected discipline.", iconColor: "#f57c00" }}
          />
        </>
      )}

      <div className="dual-summary-grid dual-approval-grid">
        {/* Certificate Approval */}
        <div className="dual-col-wrapper">
          <div className="section-header dual-col-header">
            <div className="sh-left">
              <span className="sh-bar" />
              <h2 className="sh-title">Certificate Approval</h2>
            </div>
          </div>
          <GraphCard
            controls={
              <Select
                value={certApprovalTimeframe}
                onChange={(e) => {
                  setCertApprovalTimeframe(e.target.value);
                  fetchCertApprovalStats(e.target.value);
                }}
                size="small"
                className="dashboard-select"
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="overall">Overall</MenuItem>
              </Select>
            }
            chart={<PieGraph data={certApprovalPieData} />}
            summaryTitle={`Approval Summary: ${certApprovalTimeframe === "overall" ? "Overall" : (certApprovalTimeframe === "today" ? "Today" : "This Month")}`}
            stats={[
              { label: "Total", value: certApprovalStats.total, color: "#283593", bg: "rgba(40,53,147,0.08)" },
              { label: "Approved", value: certApprovalStats.approved, color: "#1976d2", bg: "rgba(25,118,210,0.08)" },
              { label: "Pending", value: certApprovalStats.pending, color: "#64b5f6", bg: "rgba(100,181,246,0.15)" },
            ]}
            footerNote={{ text: "Approval Overview." }}
          />
        </div>

        {/* Requirement Statistic */}
        {hasAccessToModule(10) && <div className="dual-col-wrapper">
          <div className="section-header dual-col-header">
            <div className="sh-left">
              <span className="sh-bar" />
              <h2 className="sh-title">Requirement Statistic</h2>
            </div>
          </div>
          <GraphCard
            controls={
              <Select
                value={reqApprovalTimeframe}
                onChange={(e) => {
                  setReqApprovalTimeframe(e.target.value);
                  fetchReqApprovalStats(e.target.value);
                }}
                size="small"
                className="dashboard-select"
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="overall">Overall</MenuItem>
              </Select>
            }
            chart={<PieGraph data={reqApprovalPieData} />}
            summaryTitle={`Requirement Summary: ${reqApprovalTimeframe === "overall" ? "Overall" : (reqApprovalTimeframe === "today" ? "Today" : "This Month")}`}
            stats={[
              { label: "Total", value: reqApprovalStats.total, color: "#283593", bg: "rgba(40,53,147,0.08)" },
              { label: "Approved", value: reqApprovalStats.approved, color: "#1976d2", bg: "rgba(25,118,210,0.08)" },
              { label: "Pending", value: reqApprovalStats.pending, color: "#64b5f6", bg: "rgba(100,181,246,0.15)" },
              { label: "Rejected", value: reqApprovalStats.rejected, color: "#607d8b", bg: "rgba(144,164,174,0.15)" },
              { label: "Disbursed", value: reqApprovalStats.disbursed, color: "#2e7d32", bg: "rgba(46,125,50,0.10)" },
            ]}
            footerNote={{ text: "Requirement statistics current status." }}
          />
        </div>}
      </div>

      {/* 7. LEAVE STATISTICS */}
      {hasAccessToModule(5) && <>
      <SectionHeader title="Leave Statistics" />
      <GraphCard
        controls={
          <Select
            value={leaveTimeframe}
            onChange={(e) => {
              setLeaveTimeframe(e.target.value);
              fetchLeaveStats(e.target.value);
            }}
            size="small"
            className="dashboard-select"
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="monthly">This Month</MenuItem>
            <MenuItem value="overall">Overall</MenuItem>
          </Select>
        }
        chart={<PieGraph data={leavePieData} />}
        summaryTitle={`Leave Summary - ${leaveTimeframe === "overall" ? "Overall" : (leaveTimeframe === "today" ? "Today" : "This Month")}`}
        stats={[
          { label: "Total Applied", value: leaveStats.total, color: "#283593", bg: "rgba(40,53,147,0.08)" },
          { label: "Approved", value: leaveStats.approved, color: "#1976d2", bg: "rgba(25,118,210,0.08)" },
          { label: "Pending", value: leaveStats.pending, color: "#64b5f6", bg: "rgba(100,181,246,0.15)" },
          { label: "Rejected", value: leaveStats.rejected, color: "#90a4ae", bg: "rgba(144,164,174,0.15)" },
        ]}
        footerNote={{ text: "Leave application statistics.", iconColor: "#ff5722" }}
      />
      </>}

        </> /* end non-client branch */
      )} {/* end isClient ternary */}
    </div>
  );
}

